import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import chatWebSocketService from '../../services/chatWebSocket';
import { addSupportMessage, getOrderMessages } from '../../services/chatApi';
import './ChatWidget.css';

/**
 * Виджет чата поддержки
 */
const ChatWidget = ({ orderId = null, userEmail = null }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Автоскролл к последнему сообщению
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Инициализация чата при открытии
  useEffect(() => {
    if (isOpen && !isConnected) {
      initializeChat();
    }

    return () => {
      if (isConnected) {
        chatWebSocketService.offAgentMessage();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [isOpen]);
  
  // Скрываем виджет на странице агента
  if (location.pathname === '/support-agent') {
    return null;
  }

  /**
   * Инициализация чата
   */
  const initializeChat = async () => {
    try {
      setIsLoading(true);

      // Если есть orderId, загружаем историю сообщений
      if (orderId) {
        const history = await getOrderMessages(orderId);
        const formattedMessages = history.map((msg) => ({
          id: msg.id,
          text: msg.message,
          isFromUser: msg.isFromUser,
          timestamp: new Date(msg.createdAt),
        }));
        setMessages(formattedMessages);

        // Подключаемся к WebSocket для real-time обновлений
        if (userEmail) {
          connectWebSocket(orderId, userEmail);
        }
      } else {
        // Приветственное сообщение для нового чата
        setMessages([
          {
            id: 'welcome',
            text: 'Hello! How can I help you today?',
            isFromUser: false,
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error) {
      console.error('Error initializing chat:', error);
      setMessages([
        {
          id: 'error',
          text: 'Sorry, there was an error loading the chat. Please try again.',
          isFromUser: false,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Подключение к WebSocket
   */
  const connectWebSocket = (orderId, email) => {
    chatWebSocketService.connect();

    chatWebSocketService.joinOrderChat(orderId, email, (data) => {
      console.log('Joined chat successfully:', data);
      setIsConnected(true);
    });

    // Подписка на сообщения от агента
    chatWebSocketService.onAgentMessage((data) => {
      // Показываем индикатор печати
      setIsTyping(true);
      
      // Убираем индикатор и показываем сообщение через 1-2 секунды
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        const newMessage = {
          id: Date.now(),
          text: data.message,
          isFromUser: false,
          timestamp: new Date(data.timestamp || Date.now()),
        };
        setMessages((prev) => [...prev, newMessage]);
      }, 1000 + Math.random() * 1000); // 1-2 секунды
    });
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  /**
   * Отправка сообщения
   */
  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!message.trim()) return;

    const messageText = message.trim();
    const newMessage = {
      id: Date.now(),
      text: messageText,
      isFromUser: true,
      timestamp: new Date(),
    };

    // Добавляем сообщение в UI сразу
    setMessages((prev) => [...prev, newMessage]);
    setMessage('');

    try {
      // Если подключен WebSocket, отправляем через него
      if (isConnected && orderId) {
        chatWebSocketService.sendMessage(messageText, (data) => {
          console.log('Message sent via WebSocket:', data);
        });
      } else {
        // Иначе используем REST API
        const messageData = orderId
          ? {
              orderId,
              message: messageText,
              isFromUser: true,
            }
          : {
              email: userEmail || 'guest@example.com',
              phone: '+1234567890',
              country: 'US',
              state: 'CA',
              message: messageText,
              isFromUser: true,
            };

        await addSupportMessage(messageData);
        console.log('Message sent via REST API');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Показываем ошибку пользователю
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: 'Sorry, your message could not be sent. Please try again.',
          isFromUser: false,
          timestamp: new Date(),
        },
      ]);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <div className={`chat-widget-button ${isOpen ? 'hidden' : ''}`} onClick={toggleChat}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 2H4C2.9 2 2.01 2.9 2.01 4L2 22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM18 14H6V12H18V14ZM18 11H6V9H18V11ZM18 8H6V6H18V8Z" fill="white"/>
        </svg>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-widget-window">
          {/* Header */}
          <div className="chat-widget-header">
            <div className="chat-header-info">
              <div className="chat-avatar">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="16" fill="#ffffff"/>
                  <path d="M16 8C13.79 8 12 9.79 12 12C12 14.21 13.79 16 16 16C18.21 16 20 14.21 20 12C20 9.79 18.21 8 16 8ZM16 18C12.67 18 6 19.67 6 23V24C6 24.55 6.45 25 7 25H25C25.55 25 26 24.55 26 24V23C26 19.67 19.33 18 16 18Z" fill="#3AB242"/>
                </svg>
              </div>
              <div className="chat-header-text">
                <h3 className="chat-title">Support</h3>
                <p className="chat-status">Online</p>
              </div>
            </div>
            <button className="chat-close-button" onClick={toggleChat}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5L5 15M5 5L15 15" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="chat-widget-messages">
            {isLoading ? (
              <div className="chat-loading">
                <p>Loading messages...</p>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`chat-message ${msg.isFromUser ? 'user-message' : 'support-message'}`}
                  >
                    <div className="message-bubble">
                      <p className="message-text">{msg.text}</p>
                      <span className="message-time">
                        {msg.timestamp.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                ))}
                
                {/* Typing Indicator */}
                {isTyping && (
                  <div className="typing-indicator">
                    <div className="typing-bubble">
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <form className="chat-widget-input" onSubmit={handleSendMessage}>
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              className="chat-input-field"
            />
            <button type="submit" className="chat-send-button">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 10L18 2L10 18L8 11L2 10Z" fill="#3AB242" stroke="#3AB242" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
